import type { FC, ReactNode } from 'react'
import type { Schematic } from '@/types'

import Image from 'next/image'
import { cn } from '@/lib/utils'
import { DotGrid, SchematicDiagram } from '@/components/content/schematic-figure'

interface CoverPlateProps {
    id: string
    title: string
    schematic: Schematic | null
    cover: string | null
    coverAlt: string | null
    label?: string
    number?: string
    className?: string
    sizes?: string
    priority?: boolean
}

/**
 * Listing cover. Uses the entry's cover image when there is one, otherwise
 * an abstract plate drawn from its architecture schematic, otherwise a
 * typographic plate, so a missing image never leaves a gap.
 */
const CoverPlate: FC<CoverPlateProps> = ({
    id,
    title,
    schematic,
    cover,
    coverAlt,
    label,
    number,
    className,
    sizes = '(min-width: 768px) 50vw, 100vw',
    priority = false
}): ReactNode => {
    const isRemote = cover ? /^https?:\/\//.test(cover) : false

    return (
        <div className={cn('relative aspect-[4/3] overflow-hidden border border-border bg-surface', className)}>
            {cover ? (
                <Image
                    src={cover}
                    alt={coverAlt ?? ''}
                    fill
                    sizes={sizes}
                    priority={priority}
                    unoptimized={isRemote}
                    className='object-cover transition-transform duration-700 ease-editorial group-hover:scale-[1.015]'
                />
            ) : (
                <>
                    <DotGrid id={`${id}-cover`} />

                    {schematic ? (
                        <div className='absolute inset-0 flex items-center justify-center px-[7%] pt-[12%] pb-[9%]'>
                            <SchematicDiagram
                                schematic={schematic}
                                id={`${id}-cover`}
                                compact
                                className='h-full w-full transition-transform duration-700 ease-editorial group-hover:scale-[1.02]'
                            />
                        </div>
                    ) : (
                        <span
                            aria-hidden='true'
                            className='absolute inset-0 flex items-center justify-center font-heading text-[8rem] italic leading-none text-border-strong'
                        >
                            {title.charAt(0)}
                        </span>
                    )}
                </>
            )}

            {(label || number) && (
                <div className='pointer-events-none absolute inset-x-0 top-0 flex items-center justify-between gap-3 px-4 py-3 font-mono text-[0.6875rem] uppercase tracking-label text-muted-foreground'>
                    {number && <span className='bg-surface/90 text-foreground'>No. {number}</span>}
                    {label && <span className='ml-auto truncate bg-surface/90'>{label}</span>}
                </div>
            )}
        </div>
    )
}

export default CoverPlate
